import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import { listBaskets, getCompetitorData, fetchCompetitorData, parseCsvFile, parseAsinListCsv, fetchCompetitorDataFromList } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/css/pricingBreakdown.css';

/* ─────────────────────────────────────────────────────────────────────────────
   Helper: "How to read this" caption (mirrors Streamlit's how_to_read)
   ───────────────────────────────────────────────────────────────────────────── */
const HowToRead = ({ text }) => (
    <div className="pb-how-to-read">
        💡 <strong>How to read this:</strong> {text}
    </div>
);

/* ─────────────────────────────────────────────────────────────────────────────
   Utility: price ceiling (105% of max, matching Streamlit's _price_ceiling)
   ───────────────────────────────────────────────────────────────────────────── */
const priceCeiling = (rows) => {
    const vals = rows.map(r => Number(r.product_price)).filter(v => !isNaN(v) && v > 0);
    const origVals = rows.map(r => Number(r.product_original_price)).filter(v => !isNaN(v) && v > 0);
    const all = [...vals, ...origVals];
    if (all.length === 0) return 1;
    return Math.max(...all) * 1.05;
};

function PricingBreakdown() {
    const [baskets, setBaskets] = useState([]);
    const [selectedBasket, setSelectedBasket] = useState(null);
    const [data, setData] = useState([]);
    const [period, setPeriod] = useState('week');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [csvName, setCsvName] = useState(null);

    // ── Advanced Filters ──
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [brandsInitialized, setBrandsInitialized] = useState(false);
    // Default to current year 2026 as requested by user ("Ajustar las graficas para que muestren la info del año actual (2026)")
    const [dateFrom, setDateFrom] = useState('2026-01-01');
    const [dateTo, setDateTo] = useState('');
    const [minDiscountPct, setMinDiscountPct] = useState(5);

    // ── Price & Discount Timeline (Leonel) specific controls ──
    const [pdtProduct, setPdtProduct] = useState('');
    const [pdtDiscountTypes, setPdtDiscountTypes] = useState(['Coupon', 'Amazon Deal', 'Price-drop']);

    // ── Promotions Table optional toggles ──
    const [showCoupons, setShowCoupons] = useState(false);
    const [showDeals, setShowDeals] = useState(false);

    // ── Chart style toggle (Step vs Continuous/BSR) ──
    const [chartStyle, setChartStyle] = useState('step');
    const lineShape = chartStyle === 'step' ? 'hv' : 'linear';

    const accountId = localStorage.getItem('selectedAccountValue') || '';

    const loadBaskets = useCallback(async () => {
        if (!accountId) return;
        try {
            const res = await listBaskets(accountId);
            setBaskets(res.data || []);
        } catch (err) { console.error(err); }
    }, [accountId]);

    useEffect(() => { loadBaskets(); }, [loadBaskets]);

    const loadData = useCallback(async () => {
        if (!selectedBasket || !accountId || selectedBasket.id === 'csv') return;
        setLoading(true);
        try {
            const res = await getCompetitorData(selectedBasket.id, accountId);
            setData(res.data || []);
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to load basket pricing data.');
        }
        finally { setLoading(false); }
    }, [selectedBasket, accountId]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleCsvUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true); setError('');
        try {
            const parsed = await parseCsvFile(file);
            const parsedRows = parsed.data || [];

            const hasHistoricalPricing = parsedRows.some(r => r.date && r.product_price != null);
            if (hasHistoricalPricing) {
                setData(parsedRows);
                setCsvName(file.name);
                setSelectedBasket({ id: 'csv', name: file.name });
                return;
            }

            const asinList = await parseAsinListCsv(file);
            const asinsList = asinList.data || [];
            if (!asinsList.length) throw new Error('CSV has no valid rows for pricing history or ASIN list.');
            if (!accountId) throw new Error('Select an Amazon Ads account before uploading an ASIN list CSV.');

            const fetched = await fetchCompetitorDataFromList(asinsList, accountId, 'US');
            const rows = fetched.data || [];
            if (!rows.length) {
                throw new Error(fetched?.error || fetched?.message || 'No data returned for ASIN list.');
            }

            setData(rows);
            setCsvName(file.name);
            setSelectedBasket({ id: 'csv', name: file.name });
            if (Array.isArray(fetched?.diagnostics?.failedItems) && fetched.diagnostics.failedItems.length > 0) {
                console.table(fetched.diagnostics.failedItems);
            }
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || 'Error parsing CSV: ' + err.message);
        }
        finally { setLoading(false); }
    };

    const handleClearCsv = () => { setCsvName(null); setSelectedBasket(null); setData([]); };

    const handleFetchData = async () => {
        if (!selectedBasket || selectedBasket.id === 'csv') return;
        if (!accountId) {
            setError('Select an Amazon Ads account before fetching data.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            await fetchCompetitorData(selectedBasket.id, accountId);
            const refreshed = await getCompetitorData(selectedBasket.id, accountId);
            setData(refreshed.data || []);
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to fetch data.');
        } finally {
            setLoading(false);
        }
    };

    const asins = useMemo(() => [...new Set(data.map(r => r.asin).filter(Boolean))], [data]);
    const allBrands = useMemo(() => [...new Set(data.map(r => r.brand).filter(Boolean))].sort(), [data]);
    const allYears = useMemo(() => [...new Set(data.map(r => r.date ? String(r.date).substring(0, 4) : null).filter(Boolean))].sort(), [data]);

    // Initialize selectedBrands and selectedTopYears when data loads
    useEffect(() => {
        if (allBrands.length > 0 && !brandsInitialized) {
            setSelectedBrands(allBrands);
            setBrandsInitialized(true);
        }
    }, [allBrands, brandsInitialized]);

    // Reset when basket changes
    useEffect(() => {
        setBrandsInitialized(false);
        setSelectedBrands([]);
        setDateFrom('2026-01-01');
        setDateTo('');
        setPdtProduct('');
        setSelectedDate('');
    }, [selectedBasket]);

    const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const getYearWeek = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const startOfYear = new Date(year, 0, 1);
        const days = Math.floor((date - startOfYear) / (24 * 60 * 60 * 1000));
        const week = Math.ceil((days + startOfYear.getDay() + 1) / 7);
        return `${year}-W${String(week).padStart(2, '0')}`;
    };

    const getWeekLabel = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const startOfYear = new Date(year, 0, 1);
        const days = Math.floor((date - startOfYear) / (24 * 60 * 60 * 1000));
        const week = Math.ceil((days + startOfYear.getDay() + 1) / 7);
        return `${MONTH_NAMES[date.getMonth()]} ${year} Wk${week}`;
    };

    const getMonthLabel = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
    };

    // Clave ordenable para dedup en agregación (YYYY-Www / YYYY-MM)
    const getXKey = (r) => {
        if (period === 'day') return r.date;
        if (period === 'month') return r.date ? r.date.substring(0, 7) : '';
        return getYearWeek(r.date) || '';
    };

    // Etiqueta de display para el eje X de Plotly
    const getXValue = (r) => {
        if (period === 'day') return r.date;
        if (period === 'month') return getMonthLabel(r.date);
        return getWeekLabel(r.date) || `Wk ${r.week}`;
    };

    // Ordenar por fecha real, no por etiqueta
    const sortByDate = (arr) => arr.sort((a, b) => new Date(a.date) - new Date(b.date));

    const aggregateByWeek = (asinData) => {
        if (period !== 'week') return asinData;
        const weekMap = {};
        asinData.forEach(r => { weekMap[getXKey(r)] = r; });
        return sortByDate(Object.values(weekMap));
    };

    const aggregateByMonth = (asinData) => {
        if (period !== 'month') return asinData;
        const monthMap = {};
        asinData.forEach(r => { monthMap[getXKey(r)] = r; });
        return sortByDate(Object.values(monthMap));
    };

    const aggregateData = (asinData) => {
        if (period === 'month') return aggregateByMonth(asinData);
        if (period === 'week') return aggregateByWeek(asinData);
        return asinData;
    };

    /* ─────────────────────────────────────────────────────────────────────────────
       PRESERVED ORIGINAL TOP CHART: Price Evolution by ASIN (with Top Year Filter)
       ───────────────────────────────────────────────────────────────────────────── */
    const buildPriceTraces = () => {
        return filteredAsins.map(asin => {
            let asinData = filteredData.filter(r => r.asin === asin);
            asinData.sort((a, b) => new Date(a.date) - new Date(b.date));
            const brand = asinData[0]?.brand || 'Unknown';
            const displayData = aggregateData(asinData);
            const prices = displayData.map(r => r.product_price);
            const markerSizes = chartStyle === 'linear'
                ? prices.map((p, i) => (i === 0 || Number(p) !== Number(prices[i - 1])) ? 6 : 0)
                : prices.map(() => 5);

            return {
                x: displayData.map(r => r.date),
                y: prices,
                mode: 'lines+markers',
                line: { shape: lineShape, width: 2 },
                marker: { size: markerSizes },
                name: `${brand} - ${asin}`,
                hovertemplate: `ASIN: ${asin}<br>Price: $%{y:.2f}<br>Date: %{customdata[2]}<br>Change: %{customdata[0]:.2f}%<br>Source: %{customdata[1]}<extra></extra>`,
                customdata: displayData.map(r => [r.price_change || 0, r.data_source || 'RapidAPI', getXValue(r)]),
            };
        });
    };

    /* ─────────────────────────────────────────────────────────────────────────────
       FILTERED DATASET (Used by ALL inferior charts and tables)
       ───────────────────────────────────────────────────────────────────────────── */
    const filteredData = useMemo(() => {
        let fd = data;
        if (selectedBrands.length === 0) {
            return [];
        } else if (selectedBrands.length < allBrands.length) {
            fd = fd.filter(r => selectedBrands.includes(r.brand));
        }
        if (dateFrom) {
            fd = fd.filter(r => r.date >= dateFrom);
        }
        if (dateTo) {
            fd = fd.filter(r => r.date <= dateTo);
        }

        const map = {};
        fd.forEach(r => {
            if (!r.date || !r.asin) return;
            const key = `${r.asin}_${r.date}`;
            const existing = map[key];
            const isJS = String(r.data_source || '').toLowerCase().includes('jungle');
            if (!existing) {
                map[key] = r;
            } else {
                const existingIsJS = String(existing.data_source || '').toLowerCase().includes('jungle');
                if (isJS || (!isJS && !existingIsJS)) {
                    map[key] = r;
                }
            }
        });
        return Object.values(map).sort((a, b) => new Date(a.date) - new Date(b.date));
    }, [data, selectedBrands, allBrands, dateFrom, dateTo]);

    const filteredAsins = useMemo(() => [...new Set(filteredData.map(r => r.asin).filter(Boolean))], [filteredData]);

    // Product options for Price & Discount Timeline
    const pdtOptions = useMemo(() => {
        const seen = new Set();
        const opts = [];
        filteredData.forEach(r => {
            if (r.asin && !seen.has(r.asin)) {
                seen.add(r.asin);
                const label = `${r.brand || 'Unknown'} — ${r.asin}`;
                opts.push({ label, asin: r.asin });
            }
        });
        return opts.sort((a, b) => a.label.localeCompare(b.label));
    }, [filteredData]);

    useEffect(() => {
        if (pdtOptions.length > 0 && (!pdtProduct || !pdtOptions.some(o => o.asin === pdtProduct))) {
            setPdtProduct(pdtOptions[0].asin);
        }
    }, [pdtOptions, pdtProduct]);

    /* ─────────────────────────────────────────────────────────────────────────────
       PROMOTIONS TABLE & SNAPSHOT (Leonel's render_promotions_overview)
       ───────────────────────────────────────────────────────────────────────────── */
    const promotionsSnapshot = useMemo(() => {
        if (filteredData.length === 0) return { list: [], totalProducts: 0 };
        const latestDate = [...new Set(filteredData.map(r => r.date))].sort().pop();
        const snapshot = filteredData.filter(r => r.date === latestDate);

        const promos = snapshot.filter(r => {
            const disc = Number(r.discount) || 0;
            const hasCoupon = r.coupon_text || (r.coupon && r.coupon !== '');
            return disc >= 5 || hasCoupon;
        });

        return {
            list: promos.sort((a, b) => (Number(b.discount) || 0) - (Number(a.discount) || 0)),
            totalProducts: snapshot.length,
        };
    }, [filteredData]);

    const activeCouponsList = useMemo(() => {
        if (filteredData.length === 0) return [];
        const latestDate = [...new Set(filteredData.map(r => r.date))].sort().pop();
        return filteredData.filter(r => r.date === latestDate && (r.coupon_text || r.coupon));
    }, [filteredData]);

    const activeDealsList = useMemo(() => {
        if (filteredData.length === 0) return [];
        const latestDate = [...new Set(filteredData.map(r => r.date))].sort().pop();
        return filteredData.filter(r => r.date === latestDate && (r.deal_type || r.deal_badge || r.is_deal));
    }, [filteredData]);

    /* ─────────────────────────────────────────────────────────────────────────────
       PRICE & DISCOUNT TIMELINE CALCULATIONS (Leonel's discount_timeline.py)
       ───────────────────────────────────────────────────────────────────────────── */
    const buildTimelineFigure = () => {
        if (!pdtProduct) return null;
        const asinData = filteredData.filter(r => r.asin === pdtProduct).sort((a, b) => new Date(a.date) - new Date(b.date));
        if (asinData.length === 0) return null;

        const brand = asinData[0]?.brand || 'Unknown';
        const displayData = aggregateData(asinData);

        const hasOrigPrice = displayData.some(r => r.product_original_price != null && Number(r.product_original_price) > 0);
        const traces = [];

        // Row 1: List Price trace
        if (hasOrigPrice) {
            traces.push({
                x: displayData.map(r => r.date),
                y: displayData.map(r => Number(r.product_original_price) || null),
                mode: 'lines',
                name: 'List price',
                line: { color: 'rgba(160,160,170,0.55)', width: 1.5 },
                hoverinfo: 'skip',
                xaxis: 'x',
                yaxis: 'y',
            });
        }

        // Row 1: Selling Price trace with shaded fill to list price
        const sellingPrices = displayData.map(r => r.product_price);
        const sellingMarkerSizes = chartStyle === 'linear'
            ? sellingPrices.map((p, i) => (i === 0 || Number(p) !== Number(sellingPrices[i - 1])) ? 6 : 0)
            : sellingPrices.map(() => 6);

        traces.push({
            x: displayData.map(r => r.date),
            y: sellingPrices,
            mode: 'lines+markers',
            name: 'Selling price',
            line: { color: '#4C8DF6', width: 2.5 },
            marker: { size: sellingMarkerSizes },
            fill: hasOrigPrice ? 'tonexty' : undefined,
            fillcolor: 'rgba(76,141,246,0.10)',
            customdata: displayData.map(r => [
                Number(r.product_original_price) || 0,
                Number(r.discount) || 0,
                r.data_source || 'RapidAPI',
                getXValue(r),
            ]),
            hovertemplate: `<b>${brand} — ${pdtProduct}</b><br>Price: $%{y:.2f}<br>List: $%{customdata[0]:.2f}<br>Discount: %{customdata[1]:.0f}%<br>Date: %{customdata[3]}<br>Source: %{customdata[2]}<extra></extra>`,
            xaxis: 'x',
            yaxis: 'y',
        });

        // Helper to check if a value is actually truthy (ignoring "false", "0", "null" strings)
        const isTruthy = (val) => {
            if (!val) return false;
            const s = String(val).toLowerCase().trim();
            return s !== 'false' && s !== '0' && s !== 'null' && s !== 'none' && s !== 'undefined';
        };

        // Row 2: Discount segments Gantt lanes (Coupon, Amazon Deal, Price-drop)
        const periods = displayData.map(r => r.date);
        
        // Helper to find runs of consecutive active indices
        const getRuns = (mask) => {
            const runs = [];
            let current = null;
            mask.forEach((active, idx) => {
                if (active) {
                    if (!current) current = { startIdx: idx, endIdx: idx };
                    else current.endIdx = idx;
                } else if (current) {
                    runs.push(current);
                    current = null;
                }
            });
            if (current) runs.push(current);
            return runs;
        };

        // 1. Coupon lane (#8AB4F8)
        if (pdtDiscountTypes.includes('Coupon')) {
            const mask = displayData.map(r => isTruthy(r.coupon_text) || isTruthy(r.coupon));
            const runs = getRuns(mask);
            runs.forEach((run, idx) => {
                const singleDay = run.startIdx === run.endIdx;
                const startX = periods[run.startIdx];
                const endX = periods[run.endIdx];
                const startLabel = getXValue(displayData[run.startIdx]);
                const endLabel = getXValue(displayData[run.endIdx]);
                const couponTxt = displayData[run.startIdx].coupon_text || displayData[run.startIdx].coupon || 'Coupon';

                if (singleDay) {
                    traces.push({
                        x: [startX], y: ['Coupon'], mode: 'markers', name: 'Coupon', legendgroup: 'Coupon', showlegend: idx === 0,
                        marker: { color: '#8AB4F8', size: 9, symbol: 'diamond' },
                        hovertext: `${couponTxt}<br>${startLabel}`, hoverinfo: 'text', xaxis: 'x', yaxis: 'y2',
                    });
                } else {
                    traces.push({
                        x: [startX, endX], y: ['Coupon', 'Coupon'], mode: 'lines', name: 'Coupon', legendgroup: 'Coupon', showlegend: idx === 0,
                        line: { color: '#8AB4F8', width: 8 },
                        hovertext: `${couponTxt}<br>${startLabel} → ${endLabel}`, hoverinfo: 'text', xaxis: 'x', yaxis: 'y2',
                    });
                }
            });
        }

        // 2. Amazon Deal lane (#F6AD7B)
        if (pdtDiscountTypes.includes('Amazon Deal')) {
            const mask = displayData.map(r => isTruthy(r.deal_type) || isTruthy(r.deal_badge) || isTruthy(r.is_deal));
            const runs = getRuns(mask);
            runs.forEach((run, idx) => {
                const singleDay = run.startIdx === run.endIdx;
                const startX = periods[run.startIdx];
                const endX = periods[run.endIdx];
                const startLabel = getXValue(displayData[run.startIdx]);
                const endLabel = getXValue(displayData[run.endIdx]);
                const dealTxt = displayData[run.startIdx].deal_type || displayData[run.startIdx].deal_badge || 'Amazon Deal';

                if (singleDay) {
                    traces.push({
                        x: [startX], y: ['Amazon Deal'], mode: 'markers', name: 'Amazon Deal', legendgroup: 'Amazon Deal', showlegend: idx === 0,
                        marker: { color: '#F6AD7B', size: 9, symbol: 'diamond' },
                        hovertext: `${dealTxt}<br>${startLabel}`, hoverinfo: 'text', xaxis: 'x', yaxis: 'y2',
                    });
                } else {
                    traces.push({
                        x: [startX, endX], y: ['Amazon Deal', 'Amazon Deal'], mode: 'lines', name: 'Amazon Deal', legendgroup: 'Amazon Deal', showlegend: idx === 0,
                        line: { color: '#F6AD7B', width: 8 },
                        hovertext: `${dealTxt}<br>${startLabel} → ${endLabel}`, hoverinfo: 'text', xaxis: 'x', yaxis: 'y2',
                    });
                }
            });
        }

        // 3. Price-drop lane (#8DD6A0) — using minDiscountPct
        if (pdtDiscountTypes.includes('Price-drop')) {
            const mask = displayData.map(r => {
                const orig = Number(r.product_original_price);
                const price = Number(r.product_price);
                if (orig > 0 && price > 0) {
                    const depth = ((orig - price) / orig) * 100;
                    return depth >= minDiscountPct;
                }
                return (Number(r.discount) || 0) >= minDiscountPct;
            });
            const runs = getRuns(mask);
            runs.forEach((run, idx) => {
                const singleDay = run.startIdx === run.endIdx;
                const startX = periods[run.startIdx];
                const endX = periods[run.endIdx];
                const startLabel = getXValue(displayData[run.startIdx]);
                const endLabel = getXValue(displayData[run.endIdx]);
                const record = displayData[run.startIdx];
                const orig = Number(record.product_original_price);
                const price = Number(record.product_price);
                const depth = orig > 0 ? ((orig - price) / orig) * 100 : (Number(record.discount) || 0);

                if (singleDay) {
                    traces.push({
                        x: [startX], y: ['Price-drop'], mode: 'markers', name: 'Price-drop', legendgroup: 'Price-drop', showlegend: idx === 0,
                        marker: { color: '#8DD6A0', size: 9, symbol: 'diamond' },
                        hovertext: `Price-drop · ${depth.toFixed(0)}% off<br>${startLabel}`, hoverinfo: 'text', xaxis: 'x', yaxis: 'y2',
                    });
                } else {
                    traces.push({
                        x: [startX, endX], y: ['Price-drop', 'Price-drop'], mode: 'lines', name: 'Price-drop', legendgroup: 'Price-drop', showlegend: idx === 0,
                        line: { color: '#8DD6A0', width: 8 },
                        hovertext: `Price-drop · ${depth.toFixed(0)}% off<br>${startLabel} → ${endLabel}`, hoverinfo: 'text', xaxis: 'x', yaxis: 'y2',
                    });
                }
            });
        }

        const activeLanes = [];
        traces.forEach(t => {
            if (t.yaxis === 'y2' && !activeLanes.includes(t.y[0])) {
                activeLanes.push(t.y[0]);
            }
        });
        const categoryarray = ['Price-drop', 'Amazon Deal', 'Coupon'].filter(c => activeLanes.includes(c));

        return {
            data: traces,
            layout: {
                height: 520,
                hovermode: 'closest',
                margin: { l: 130, r: 25, t: 35, b: 45 },
                legend: { orientation: 'h', yanchor: 'bottom', y: 1.02, x: 0 },
                yaxis: {
                    domain: [0.30, 1],
                    title: { text: 'Price (USD)', standoff: 10 },
                    tickprefix: '$',
                    automargin: true,
                    range: [0, priceCeiling(displayData)],
                },
                yaxis2: {
                    domain: [0, 0.22],
                    title: { text: 'Discounts', standoff: 15 },
                    showgrid: false,
                    type: 'category',
                    automargin: true,
                    categoryorder: 'array',
                    categoryarray: categoryarray.length > 0 ? categoryarray : [''],
                },
                xaxis: {
                    anchor: 'y2',
                    title: { text: period === 'day' ? 'Date' : period === 'month' ? 'Month' : 'Week' },
                    type: 'date',
                    tickformat: period === 'month' ? '%b %Y' : '%b %d, %Y',
                },
            },
        };
    };

    /* ─────────────────────────────────────────────────────────────────────────────
       INFERIOR CHARTS TRACES & TABLES (Using filteredData)
       ───────────────────────────────────────────────────────────────────────────── */
    const buildPivotTable = () => {
        const keys = [...new Set(filteredData.map(r => getXKey(r)))].sort();
        const periodCol = period === 'day' ? 'Date' : period === 'month' ? 'Month' : 'Week';
        return keys.map(key => {
            const row = { [periodCol]: key };
            filteredAsins.forEach(asin => {
                const match = filteredData.find(r => r.asin === asin && getXKey(r) === key);
                const brand = filteredData.find(r => r.asin === asin)?.brand || '';
                if (match) {
                    const priceStr = match.product_price != null ? `$${Number(match.product_price).toFixed(2)}` : '—';
                    const sourceTag = match.data_source === 'JungleScout' || match.data_source === 'Jungle Scout' ? ' (JS)' : ' (RA)';
                    row[`${brand} - ${asin}`] = match.product_price != null ? `${priceStr}${sourceTag}` : '—';
                } else {
                    row[`${brand} - ${asin}`] = '—';
                }
            });
            return row;
        });
    };

    const buildVariationTraces = () => {
        return filteredAsins.map(asin => {
            const asinData = filteredData.filter(r => r.asin === asin).sort((a, b) => new Date(a.date) - new Date(b.date));
            const brand = asinData[0]?.brand || 'Unknown';
            const displayData = aggregateData(asinData);
            const changes = displayData.map(r => r.price_change || 0);
            const markerSizes = chartStyle === 'linear'
                ? changes.map((v, i) => (i === 0 || Number(v) !== Number(changes[i - 1])) ? 6 : 0)
                : changes.map(() => 5);

            return {
                x: displayData.map(r => r.date),
                y: changes,
                mode: 'lines+markers',
                line: { shape: lineShape, width: 2 },
                marker: { size: markerSizes },
                name: `${brand} - ${asin}`,
                hovertemplate: `ASIN: ${asin}<br>Date: %{customdata[0]}<br>Change: %{y:.2f}%<br>Source: %{customdata[1]}<extra></extra>`,
                customdata: displayData.map(r => [getXValue(r), r.data_source || 'RapidAPI']),
            };
        });
    };

    const availableDates = useMemo(() => {
        return [...new Set(filteredData.map(r => r.date).filter(Boolean))].sort((a, b) => new Date(b) - new Date(a));
    }, [filteredData]);

    useEffect(() => {
        if (availableDates.length > 0 && (!selectedDate || !availableDates.includes(selectedDate))) {
            setSelectedDate(availableDates[0]);
        }
    }, [availableDates, selectedDate]);

    const handleNextDate = () => {
        const idx = availableDates.indexOf(selectedDate);
        if (idx > 0) {
            setSelectedDate(availableDates[idx - 1]);
        }
    };

    const handlePrevDate = () => {
        const idx = availableDates.indexOf(selectedDate);
        if (idx !== -1 && idx < availableDates.length - 1) {
            setSelectedDate(availableDates[idx + 1]);
        }
    };

    const matrixBrands = useMemo(() => [...new Set(filteredData.map(r => r.brand).filter(Boolean))].sort(), [filteredData]);

    const matrixRows = useMemo(() => {
        if (!selectedDate) return [];
        const dateData = filteredData.filter(r => r.date === selectedDate);
        const uniqueAsins = [...new Set(dateData.map(r => r.asin))].sort();
        return uniqueAsins.map(asin => {
            const row = { asin };
            matrixBrands.forEach(b => row[b] = '—');
            const asinRecords = dateData.filter(r => r.asin === asin);
            const record = asinRecords.find(r => r.price_change != null) || asinRecords[0];
            if (record && record.brand) {
                const matchedBrand = matrixBrands.find(b => b.toLowerCase() === record.brand.toLowerCase());
                if (matchedBrand) {
                    row[matchedBrand] = record.price_change != null ? `${Number(record.price_change).toFixed(2)}%` : '—';
                }
            }
            return row;
        });
    }, [filteredData, selectedDate, matrixBrands]);

    const providerHint = useMemo(() => {
        if (!error) return '';
        const normalized = String(error).toLowerCase();
        if (normalized.includes('not subscribed')) return 'Live provider access is not subscribed. Use a historical tracking CSV or enable RapidAPI subscription for live ASIN-list fetches.';
        if (normalized.includes('too many requests') || normalized.includes('429')) return 'Provider rate limit reached. Retry in a few minutes or reduce ASIN volume per request.';
        if (normalized.includes('external provider failed')) return 'The external provider failed for this request. If this is a basket with cached snapshots, use Overview fallback data.';
        return '';
    }, [error]);

    const pivotData = filteredData.length > 0 ? buildPivotTable() : [];
    const pivotColumns = pivotData.length > 0 ? Object.keys(pivotData[0]) : [];

    const handleBrandToggle = (brand) => {
        setSelectedBrands(prev => prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]);
    };

    const handleDiscountTypeToggle = (type) => {
        setPdtDiscountTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
    };

    const timelineFig = buildTimelineFigure();

    return (
        <Layout>
            <div className="container-fluid pb-container">
                <h2 className="pb-page-title">Pricing Breakdown</h2>
                <p className="pb-page-subtitle">
                    Comprehensive competitor pricing analysis, discount timelines, and promotional tracking.
                </p>

                {error && <div className="alert alert-danger">{error}</div>}
                {providerHint && <div className="alert alert-warning">{providerHint}</div>}

                {/* Top Controls */}
                <div className="row mb-3">
                    <div className="col-md-4">
                        <select className="form-select" value={selectedBasket?.id || ''}
                            onChange={e => setSelectedBasket(baskets.find(b => b.id === parseInt(e.target.value)) || null)}>
                            <option value="">Select basket...</option>
                            {baskets.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <div className="d-flex align-items-center gap-2">
                            <label className="btn btn-outline-info btn-sm mb-0" style={{ cursor: 'pointer' }}>
                                <i className="bi bi-filetype-csv me-1"></i>Upload CSV
                                <input type="file" accept=".csv" hidden onChange={handleCsvUpload} />
                            </label>
                            {csvName && (
                                <span className="badge bg-success d-flex align-items-center gap-1">
                                    {csvName}
                                    <i className="bi bi-x-lg" style={{ cursor: 'pointer' }} onClick={handleClearCsv}></i>
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="btn-group btn-group-sm w-100">
                            <button className={`btn ${period === 'week' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPeriod('week')}>Weekly</button>
                            <button className={`btn ${period === 'month' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPeriod('month')}>Monthly</button>
                            <button className={`btn ${period === 'day' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPeriod('day')}>Daily</button>
                        </div>
                    </div>
                    <div className="col-md-2">
                        <button
                            className="btn btn-success btn-sm w-100"
                            onClick={handleFetchData}
                            disabled={loading || !selectedBasket || selectedBasket.id === 'csv'}
                            title="Re-fetch latest data from RapidAPI"
                        >
                            {loading ? 'Fetching...' : 'Fetch Data'}
                        </button>
                    </div>
                </div>

                {loading && <div className="text-center my-4"><div className="spinner-border text-primary" /></div>}

                {!loading && data.length === 0 && csvName && (
                    <div className="alert alert-warning">
                        No pricing data loaded from this CSV. If it is an ASIN list, live fetch can fail when provider access is unavailable.
                    </div>
                )}

                {/* ─────────────────────────────────────────────────────────────────────────────
                    CHART 1: PRESERVED ORIGINAL PRICE EVOLUTION BY ASIN (All Data)
                    ───────────────────────────────────────────────────────────────────────────── */}
                {!loading && data.length > 0 && (
                    <div className="card pb-card mb-4">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-3">
                                    <h5 className="mb-0">Price Evolution by ASIN</h5>
                                    <div className="btn-group btn-group-sm">
                                        <button className={`btn ${chartStyle === 'step' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setChartStyle('step')} title="Stepped line chart (square transitions)">Step Graph</button>
                                        <button className={`btn ${chartStyle === 'linear' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setChartStyle('linear')} title="Continuous line chart (like BSR Ranking)">Continuous Graph</button>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="text-muted small fw-semibold">Filter Years:</span>
                                    <button
                                        type="button"
                                        className={`btn btn-sm py-1 px-2 ${(!dateFrom && !dateTo) ? 'btn-primary fw-bold shadow-sm' : 'btn-outline-secondary'}`}
                                        onClick={() => { setDateFrom(''); setDateTo(''); }}
                                    >
                                        All ({allYears.length})
                                    </button>
                                    {allYears.map(year => {
                                        const isActive = dateFrom === `${year}-01-01` && dateTo === `${year}-12-31`;
                                        return (
                                            <button
                                                key={year}
                                                type="button"
                                                className={`btn btn-sm py-1 px-2 ${isActive ? 'btn-primary fw-bold shadow-sm' : 'btn-outline-secondary'}`}
                                                onClick={() => {
                                                    setDateFrom(`${year}-01-01`);
                                                    setDateTo(`${year}-12-31`);
                                                }}
                                            >
                                                {year}
                                            </button>
                                        );
                                    })}
                                    <div className="d-flex align-items-center gap-2 border-start ps-3 ms-2 flex-wrap">
                                        <span className="text-muted small fw-semibold">Date Range:</span>
                                        <input type="date" className="form-control form-control-sm" style={{ width: '130px', height: '30px' }}
                                            value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                                        <span className="text-muted small">to</span>
                                        <input type="date" className="form-control form-control-sm" style={{ width: '130px', height: '30px' }}
                                            value={dateTo} onChange={e => setDateTo(e.target.value)} />
                                        {(dateFrom || dateTo) && (
                                            <button className="btn btn-sm btn-outline-danger py-0 px-2" style={{ fontSize: '0.75rem', height: '30px' }}
                                                onClick={() => { setDateFrom(''); setDateTo(''); }}>
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {filteredAsins.length > 0 ? (
                                <Plot
                                    data={buildPriceTraces()}
                                    layout={{
                                        height: Math.max(400, 120 * Math.ceil(filteredAsins.length / 3)),
                                        xaxis: {
                                            title: { text: period === 'day' ? 'Date' : period === 'month' ? 'Month' : 'Week' },
                                            type: 'date',
                                            tickformat: period === 'month' ? '%b %Y' : '%b %d, %Y',
                                        },
                                        yaxis: { title: { text: 'Product Price (USD)' }, tickprefix: '$' },
                                        margin: { l: 60, r: 20, t: 30, b: 70 },
                                        showlegend: true,
                                    }}
                                    config={{ responsive: true }}
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <div className="alert alert-info text-center py-4 my-2">
                                    No data matches the current date range or brand filters.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ─────────────────────────────────────────────────────────────────────────────
                    ADVANCED FILTERS BAR (Controls all inferior charts: Breakdown, Promotions, Timeline, Variation)
                    ───────────────────────────────────────────────────────────────────────────── */}
                {!loading && data.length > 0 && (
                    <>
                        <div className="card pb-filters-card mb-4">
                            <div className="pb-filters-header d-flex justify-content-between align-items-center">
                                <div>
                                    <h6>Advanced Filters (For Lower Charts & Timelines)</h6>
                                    <small>Refine by brand, date range (defaulting to 2026), and promotional depth.</small>
                                </div>
                                <span className="badge bg-info text-dark">Filtered ASINs: {filteredAsins.length} / {asins.length}</span>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    {/* Brands multiselect */}
                                    <div className="col-md-6">
                                        <span className="pb-filter-label">Brands to display</span>
                                        <div className="pb-brand-list">
                                            <div className="d-flex gap-2 mb-1">
                                                <button className="btn btn-outline-secondary btn-sm py-0" style={{ fontSize: '0.75rem' }} onClick={() => setSelectedBrands([...allBrands])}>All</button>
                                                <button className="btn btn-outline-secondary btn-sm py-0" style={{ fontSize: '0.75rem' }} onClick={() => setSelectedBrands([])}>None</button>
                                            </div>
                                            {allBrands.map(b => (
                                                <div key={b} className="form-check">
                                                    <input className="form-check-input" type="checkbox" id={`flt-brand-${b}`}
                                                        checked={selectedBrands.includes(b)}
                                                        onChange={() => handleBrandToggle(b)} />
                                                    <label className="form-check-label" htmlFor={`flt-brand-${b}`}>{b}</label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Min price-drop % */}
                                    <div className="col-md-6">
                                        <span className="pb-filter-label">Min Promotional Depth: {minDiscountPct}%</span>
                                        <input type="range" className="form-range mt-2" min="0" max="50" step="1"
                                            value={minDiscountPct} onChange={e => setMinDiscountPct(Number(e.target.value))} />
                                        <small className="pb-filter-hint">Filters Price-Drop lanes and minor percentage changes.</small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {filteredAsins.length === 0 && (
                            <div className="alert alert-info">No data matches the current filter criteria. Try adjusting the year, brand, or date range filters.</div>
                        )}

                        {/* ─────────────────────────────────────────────────────────────────────────────
                            CHART 2: UPGRADED BREAKDOWN BY ASIN (Grid view of subplots with filters)
                            ───────────────────────────────────────────────────────────────────────────── */}
                        {filteredAsins.length > 0 && (
                        <div className="card pb-card mb-4">
                            <div className="pb-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <h5 className="mb-0">Breakdown by ASIN (Filtered Grid View)</h5>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="text-muted small fw-semibold">Filter Years:</span>
                                    <button
                                        type="button"
                                        className={`btn btn-sm py-1 px-2 ${(!dateFrom && !dateTo) ? 'btn-primary fw-bold shadow-sm' : 'btn-outline-secondary'}`}
                                        onClick={() => { setDateFrom(''); setDateTo(''); }}
                                    >
                                        All ({allYears.length})
                                    </button>
                                    {allYears.map(year => {
                                        const isActive = dateFrom === `${year}-01-01` && dateTo === `${year}-12-31`;
                                        return (
                                            <button
                                                key={year}
                                                type="button"
                                                className={`btn btn-sm py-1 px-2 ${isActive ? 'btn-primary fw-bold shadow-sm' : 'btn-outline-secondary'}`}
                                                onClick={() => {
                                                    setDateFrom(`${year}-01-01`);
                                                    setDateTo(`${year}-12-31`);
                                                }}
                                            >
                                                {year}
                                            </button>
                                        );
                                    })}
                                    <div className="d-flex align-items-center gap-2 border-start ps-3 ms-2 flex-wrap">
                                        <span className="text-muted small fw-semibold">Date Range:</span>
                                        <input type="date" className="form-control form-control-sm" style={{ width: '130px', height: '30px' }}
                                            value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                                        <span className="text-muted small">to</span>
                                        <input type="date" className="form-control form-control-sm" style={{ width: '130px', height: '30px' }}
                                            value={dateTo} onChange={e => setDateTo(e.target.value)} />
                                        {(dateFrom || dateTo) && (
                                            <button className="btn btn-sm btn-outline-danger py-0 px-2" style={{ fontSize: '0.75rem', height: '30px' }}
                                                onClick={() => { setDateFrom(''); setDateTo(''); }}>
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    {filteredAsins.map(asin => {
                                        const asinData = filteredData.filter(r => r.asin === asin).sort((a, b) => new Date(a.date) - new Date(b.date));
                                        const brand = asinData[0]?.brand || 'Unknown';
                                        const displayData = aggregateData(asinData);
                                        const hasDiscount = asinData.some(r => Number(r.discount) >= minDiscountPct || r.coupon_text || r.coupon);
                                        const productUrl = asinData[0]?.product_url || '';
                                        const prices = displayData.map(r => r.product_price);
                                        const markerSizes = chartStyle === 'linear'
                                            ? prices.map((p, i) => (i === 0 || Number(p) !== Number(prices[i - 1])) ? 7 : 0)
                                            : prices.map(() => 6);

                                        return (
                                            <div className="col-12 mb-4" key={asin}>
                                                <Plot
                                                    data={[{
                                                        x: displayData.map(r => r.date),
                                                        y: prices,
                                                        mode: 'lines+markers',
                                                        line: { shape: lineShape, width: 2.5, dash: hasDiscount ? 'dot' : 'solid', color: '#3b82f6' },
                                                        marker: { size: markerSizes, color: '#3b82f6' },
                                                        hovertemplate: `Price: $%{y:.2f}<br>Date: %{customdata[0]}<br>Source: %{customdata[1]}<extra></extra>`,
                                                        customdata: asinData.map(r => [getXValue(r), r.data_source || 'RapidAPI']),
                                                    }]}
                                                    layout={{
                                                        title: {
                                                            text: productUrl ? `<a href='${productUrl}' target='_blank' style='color:#0f172a; text-decoration:none;'>↗ ${brand} - ${asin}</a>` : `${brand} - ${asin}`,
                                                            font: { size: 15, color: '#0f172a' }
                                                        },
                                                        height: 340,
                                                        xaxis: {
                                                            title: { text: period === 'day' ? 'Date' : period === 'month' ? 'Month' : 'Week' },
                                                            type: 'date',
                                                            tickformat: period === 'month' ? '%b %Y' : '%b %d, %Y',
                                                        },
                                                        yaxis: { title: { text: 'Product Price (USD)' }, tickprefix: '$', range: [0, priceCeiling(displayData)] },
                                                        margin: { l: 60, r: 25, t: 40, b: 50 },
                                                    }}
                                                    config={{ responsive: true, displayModeBar: false }}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        )}

                        {/* ─────────────────────────────────────────────────────────────────────────────
                            CHART 3: PROMOTIONS TABLE (Leonel's render_promotions_overview)
                            ───────────────────────────────────────────────────────────────────────────── */}
                        <div className="card pb-card mb-4">
                            <div className="pb-card-header d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Promotions</h5>
                                <span className="badge bg-primary">
                                    {promotionsSnapshot.list.length} of {promotionsSnapshot.totalProducts} products on promotion
                                </span>
                            </div>
                            <div className="card-body">
                                {promotionsSnapshot.list.length === 0 ? (
                                    <div className="alert alert-info mb-0">
                                        None of the {promotionsSnapshot.totalProducts} filtered products are on promotion right now (requires ≥ {minDiscountPct}% discount or an active coupon).
                                    </div>
                                ) : (
                                    <>
                                        <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                                            <strong>{promotionsSnapshot.list.length} of {promotionsSnapshot.totalProducts} products are on promotion</strong> (deepest first).
                                        </p>
                                        <div className="table-responsive">
                                            <table className="table table-sm table-hover align-middle mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>brand</th>
                                                        <th>Product</th>
                                                        <th>asin</th>
                                                        <th>Price</th>
                                                        <th>List</th>
                                                        <th>% off</th>
                                                        <th>↗</th>
                                                        <th>Coupon</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {promotionsSnapshot.list.map(item => (
                                                        <tr key={item.asin}>
                                                            <td className="fw-semibold text-dark">{item.brand || 'Unknown'}</td>
                                                            <td>
                                                                <span className="text-truncate d-block" style={{ maxWidth: '350px' }}>
                                                                    {item.product_title || item.asin}
                                                                </span>
                                                            </td>
                                                            <td className="font-monospace text-muted">{item.asin}</td>
                                                            <td className="fw-bold text-success">${Number(item.product_price).toFixed(2)}</td>
                                                            <td className="text-muted">{item.product_original_price ? `$${Number(item.product_original_price).toFixed(2)}` : '—'}</td>
                                                            <td>
                                                                {item.discount ? <span className="pb-badge-discount">{Number(item.discount).toFixed(0)}%</span> : '—'}
                                                            </td>
                                                            <td>
                                                                {item.product_url ? (
                                                                    <a href={item.product_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-link p-0 text-decoration-none">View</a>
                                                                ) : '—'}
                                                            </td>
                                                            <td>
                                                                {item.coupon_text || item.coupon ? (
                                                                    <span className="pb-badge-coupon">{item.coupon_text || item.coupon}</span>
                                                                ) : <span className="text-muted">None</span>}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}

                                {/* Optional Toggles for Coupons & Deals */}
                                <div className="d-flex gap-4 mt-3 pt-3 border-top">
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" id="chkShowCoupons"
                                            checked={showCoupons} onChange={e => setShowCoupons(e.target.checked)} />
                                        <label className="form-check-label fw-semibold" htmlFor="chkShowCoupons">🎟️ Show coupons</label>
                                    </div>
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" id="chkShowDeals"
                                            checked={showDeals} onChange={e => setShowDeals(e.target.checked)} />
                                        <label className="form-check-label fw-semibold" htmlFor="chkShowDeals">⚡ Show deals</label>
                                    </div>
                                </div>

                                {showCoupons && (
                                    <div className="mt-3 p-3 bg-light rounded border">
                                        <h6 className="fw-bold mb-2">🎟️ Active Product Coupons</h6>
                                        {activeCouponsList.length === 0 ? <small className="text-muted">No active coupons in current filtered view.</small> : (
                                            <ul className="list-group list-group-flush">
                                                {activeCouponsList.map(c => (
                                                    <li key={c.asin} className="list-group-item bg-transparent px-0 py-1 d-flex justify-content-between">
                                                        <span><strong>{c.brand}</strong> — {c.asin}</span>
                                                        <span className="pb-badge-coupon">{c.coupon_text || c.coupon}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}

                                {showDeals && (
                                    <div className="mt-3 p-3 bg-light rounded border">
                                        <h6 className="fw-bold mb-2">⚡ Active Lightning & Limited Deals</h6>
                                        {activeDealsList.length === 0 ? <small className="text-muted">No active deals in current filtered view.</small> : (
                                            <ul className="list-group list-group-flush">
                                                {activeDealsList.map(d => (
                                                    <li key={d.asin} className="list-group-item bg-transparent px-0 py-1 d-flex justify-content-between">
                                                        <span><strong>{d.brand}</strong> — {d.asin}</span>
                                                        <span className="pb-badge-discount">{d.deal_type || d.deal_badge || 'Amazon Deal'}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ─────────────────────────────────────────────────────────────────────────────
                            CHART 4: PRICE & DISCOUNT TIMELINE (Leonel's Two-Panel Chart)
                            ───────────────────────────────────────────────────────────────────────────── */}
                        <div className="card pb-card mb-4">
                            <div className="pb-card-header">
                                <h5 className="mb-1">Price & Discount Timeline</h5>
                                <HowToRead text="The top line is the selling price; the bars below show when each discount type was active. Line up a price drop with a bar to see whether a coupon, an Amazon deal, or an organic cut caused it." />
                            </div>
                            <div className="card-body">
                                {pdtOptions.length === 0 ? (
                                    <div className="alert alert-info">No product options available in current filter range.</div>
                                ) : (
                                    <>
                                        <div className="row g-3 mb-3 align-items-center">
                                            <div className="col-md-5">
                                                <span className="pb-filter-label">Product</span>
                                                <select className="form-select form-select-sm"
                                                    value={pdtProduct} onChange={e => setPdtProduct(e.target.value)}>
                                                    {pdtOptions.map(o => (
                                                        <option key={o.asin} value={o.asin}>{o.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <span className="pb-filter-label">Discount types</span>
                                                <div className="pb-pill-group">
                                                    {['Coupon', 'Amazon Deal', 'Price-drop'].map(t => (
                                                        <button key={t} type="button"
                                                            className={`pb-pill-btn ${pdtDiscountTypes.includes(t) ? 'active' : ''}`}
                                                            onClick={() => handleDiscountTypeToggle(t)}>
                                                            {t} {pdtDiscountTypes.includes(t) ? '×' : '+'}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <span className="pb-filter-label">Min price-drop %: {minDiscountPct}%</span>
                                                <input type="range" className="form-range" min="0" max="50" step="1"
                                                    value={minDiscountPct} onChange={e => setMinDiscountPct(Number(e.target.value))} />
                                            </div>
                                        </div>

                                        {timelineFig ? (
                                            <Plot
                                                data={timelineFig.data}
                                                layout={timelineFig.layout}
                                                config={{ responsive: true }}
                                                style={{ width: '100%' }}
                                            />
                                        ) : (
                                            <div className="alert alert-info">No price history found for this product in the selected period.</div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* ─────────────────────────────────────────────────────────────────────────────
                            CHART 5: PRICE HISTORY TABLE (Filtered)
                            ───────────────────────────────────────────────────────────────────────────── */}
                        {pivotData.length > 0 && (
                            <div className="card pb-card mb-4">
                                <div className="card-header pb-card-header" id="headingHistory">
                                    <h5 className="mb-0">
                                        <button className="btn btn-link text-decoration-none fw-bold" data-bs-toggle="collapse" data-bs-target="#collapseHistory">
                                            Price History Table (Click to expand)
                                        </button>
                                    </h5>
                                </div>
                                <div id="collapseHistory" className="collapse">
                                    <div className="card-body">
                                        <div className="table-responsive">
                                            <table className="table table-sm table-striped table-hover">
                                                <thead>
                                                    <tr>{pivotColumns.map(c => <th key={c}>{c}</th>)}</tr>
                                                </thead>
                                                <tbody>
                                                    {pivotData.map((row, i) => (
                                                        <tr key={i}>{pivotColumns.map(c => <td key={c}>{row[c]}</td>)}</tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ─────────────────────────────────────────────────────────────────────────────
                            CHART 6: PRICE PERCENTAGE VARIATION (by ASIN) (Filtered & Styled)
                            ───────────────────────────────────────────────────────────────────────────── */}
                        <div className="card pb-card mb-4">
                            <div className="pb-card-header">
                                <h5 className="mb-0">Price Percentage Variation (by ASIN)</h5>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label fw-bold mb-2">Select Date</label>
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <button 
                                            type="button" 
                                            className="btn btn-sm btn-outline-secondary" 
                                            onClick={handlePrevDate} 
                                            disabled={availableDates.indexOf(selectedDate) >= availableDates.length - 1}
                                            title="Go to older date"
                                        >
                                            ← Older
                                        </button>
                                        <select 
                                            className="form-select form-select-sm" 
                                            style={{ maxWidth: '240px', height: '31px' }}
                                            value={selectedDate} 
                                            onChange={e => setSelectedDate(e.target.value)}
                                        >
                                            {availableDates.map(d => {
                                                const dateObj = new Date(d + 'T00:00:00');
                                                const formatted = isNaN(dateObj.getTime()) 
                                                    ? d 
                                                    : dateObj.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
                                                return <option key={d} value={d}>{formatted}</option>;
                                            })}
                                        </select>
                                        <button 
                                            type="button" 
                                            className="btn btn-sm btn-outline-secondary" 
                                            onClick={handleNextDate} 
                                            disabled={availableDates.indexOf(selectedDate) <= 0}
                                            title="Go to newer date"
                                        >
                                            Newer →
                                        </button>
                                    </div>
                                </div>

                                {selectedDate && (() => {
                                    const dateData = filteredData.filter(r => r.date === selectedDate);
                                    const barItems = dateData
                                        .filter(r => r.price_change != null && r.price_change !== 0)
                                        .filter(r => Math.abs(Number(r.price_change)) >= minDiscountPct)
                                        .map(r => ({ label: `${r.brand || ''} — ${r.asin}`, value: Number(r.price_change), source: r.data_source || 'RapidAPI' }))
                                        .sort((a, b) => a.value - b.value);

                                    return (
                                        <>
                                            {barItems.length > 0 ? (
                                                <div className="mb-4">
                                                    <h6 className="mb-2">Price Percentage Variation (by ASIN) - {period === 'day' ? 'Day' : period === 'month' ? 'Month' : 'Week'}</h6>
                                                    <Plot
                                                        data={[{
                                                            type: 'bar',
                                                            orientation: 'h',
                                                            y: barItems.map(b => b.label),
                                                            x: barItems.map(b => b.value),
                                                            marker: { color: barItems.map(b => b.value >= 0 ? '#00c853' : '#ff1744') },
                                                            hovertemplate: '%{y}<br>Change: %{x:.2f}%<br>Source: %{customdata}<extra></extra>',
                                                            customdata: barItems.map(b => b.source),
                                                        }]}
                                                        layout={{
                                                            height: Math.max(300, barItems.length * 45),
                                                            xaxis: { title: 'Price Change (%)', zeroline: true },
                                                            yaxis: { title: 'ASIN — Brand', automargin: true },
                                                            margin: { l: 180, r: 30, t: 10, b: 50 },
                                                            font: { size: 11 },
                                                        }}
                                                        config={{ responsive: true, displayModeBar: false }}
                                                        style={{ width: '100%' }}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="alert alert-info">
                                                    No price variations ≥ {minDiscountPct}% on {selectedDate} with current filters.
                                                </div>
                                            )}

                                            <div className="p-2 mb-2 bg-light border rounded small">
                                                <span className="fw-bold">Data for selected date: {selectedDate} 00:00:00</span>
                                            </div>

                                            <div className="card">
                                                <div className="card-header" id="headingVariation">
                                                    <button className="btn btn-link text-decoration-none fw-bold" type="button" data-bs-toggle="collapse" data-bs-target="#collapseVariation" aria-expanded="true">
                                                        ▸ Show price % variation table
                                                    </button>
                                                </div>
                                                <div id="collapseVariation" className="collapse show">
                                                    <div className="card-body p-0">
                                                        <div className="table-responsive">
                                                            <table className="table table-sm table-bordered mb-0 text-center" style={{ fontSize: '0.85rem' }}>
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th className="text-start">ASIN</th>
                                                                        {matrixBrands.map(b => <th key={b}>{b}</th>)}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {matrixRows.map(row => (
                                                                        <tr key={row.asin}>
                                                                            <td className="text-start font-monospace fw-bold">{row.asin}</td>
                                                                            {matrixBrands.map(b => {
                                                                                const val = row[b];
                                                                                const num = parseFloat(val);
                                                                                let cls = 'text-muted';
                                                                                if (val && val !== '—') {
                                                                                    if (num < 0) cls = 'text-danger fw-bold';
                                                                                    else if (num > 0) cls = 'text-success fw-bold';
                                                                                    else cls = 'text-secondary';
                                                                                }
                                                                                return (
                                                                                    <td key={b} className={cls}>
                                                                                        {val}
                                                                                    </td>
                                                                                );
                                                                            })}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* ─────────────────────────────────────────────────────────────────────────────
                            CHART 7: PRICE VARIATION OVER TIME (Filtered)
                            ───────────────────────────────────────────────────────────────────────────── */}
                        {filteredAsins.length > 0 && (
                        <div className="card pb-card">
                            <div className="pb-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <h5 className="mb-0">Price Variation Chart Over Time</h5>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="text-muted small fw-semibold">Filter Years:</span>
                                    <button
                                        type="button"
                                        className={`btn btn-sm py-1 px-2 ${(!dateFrom && !dateTo) ? 'btn-primary fw-bold shadow-sm' : 'btn-outline-secondary'}`}
                                        onClick={() => { setDateFrom(''); setDateTo(''); }}
                                    >
                                        All ({allYears.length})
                                    </button>
                                    {allYears.map(year => {
                                        const isActive = dateFrom === `${year}-01-01` && dateTo === `${year}-12-31`;
                                        return (
                                            <button
                                                key={year}
                                                type="button"
                                                className={`btn btn-sm py-1 px-2 ${isActive ? 'btn-primary fw-bold shadow-sm' : 'btn-outline-secondary'}`}
                                                onClick={() => {
                                                    setDateFrom(`${year}-01-01`);
                                                    setDateTo(`${year}-12-31`);
                                                }}
                                            >
                                                {year}
                                            </button>
                                        );
                                    })}
                                    <div className="d-flex align-items-center gap-2 border-start ps-3 ms-2 flex-wrap">
                                        <span className="text-muted small fw-semibold">Date Range:</span>
                                        <input type="date" className="form-control form-control-sm" style={{ width: '130px', height: '30px' }}
                                            value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                                        <span className="text-muted small">to</span>
                                        <input type="date" className="form-control form-control-sm" style={{ width: '130px', height: '30px' }}
                                            value={dateTo} onChange={e => setDateTo(e.target.value)} />
                                        {(dateFrom || dateTo) && (
                                            <button className="btn btn-sm btn-outline-danger py-0 px-2" style={{ fontSize: '0.75rem', height: '30px' }}
                                                onClick={() => { setDateFrom(''); setDateTo(''); }}>
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <Plot
                                    data={buildVariationTraces()}
                                    layout={{
                                        height: 400,
                                        xaxis: {
                                            title: { text: period === 'day' ? 'Date' : period === 'month' ? 'Month' : 'Week' },
                                            type: 'date',
                                            tickformat: period === 'month' ? '%b %Y' : '%b %d, %Y',
                                        },
                                        yaxis: { title: { text: 'Price Change (%)' }, ticksuffix: '%' },
                                        margin: { l: 60, r: 20, t: 30, b: 55 },
                                    }}
                                    config={{ responsive: true }}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
}

export default PricingBreakdown;